// src/components/guards/GuardFormSlideOver.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { X, Copy, Eye, EyeOff } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  guardSchema,
  getNextEmployeeCode,
  generatePassword,
  getPasswordStrength,
} from '../../utils/guardHelpers';
import { useTheme } from '../../theme/ThemeContext';

const GuardFormSlideOver = ({
  isOpen,
  editingGuard,
  totalGuards,
  saving,
  onClose,
  onSubmit,
}) => {
  const { theme } = useTheme();

  // Safe fallbacks for theme colors
  const primary500 = theme?.primary?.[500] || '#6366f1';
  const primary600 = theme?.primary?.[600] || '#4f46e5';
  const secondary600 = theme?.secondary?.[600] || '#ec4899';

  const [passwordMode, setPasswordMode] = useState('auto'); // 'auto' | 'manual'
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    setError,
    clearErrors,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(guardSchema),
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      username: '',
      employee_code: '',
      status: 'active',
      date_of_joining: '',
      notes: '',
      password: '',
    },
  });

  const passwordValue = watch('password');
  const passwordStrength = useMemo(
    () => getPasswordStrength(passwordValue),
    [passwordValue]
  );

  const applyInputFocusStyle = (e) => {
    e.target.style.borderColor = primary500;
    e.target.style.boxShadow = `0 0 0 3px ${primary500}33`;
  };

  const resetInputFocusStyle = (e) => {
    e.target.style.borderColor = 'rgb(226 232 240)';
    e.target.style.boxShadow = 'none';
  };

  // Initialize form when opened / editingGuard changes
  useEffect(() => {
    if (!isOpen) return;

    clearErrors();
    setShowPassword(false);

    if (editingGuard) {
      setPasswordMode('manual');
      reset({
        first_name: editingGuard.first_name || '',
        last_name: editingGuard.last_name || '',
        email: editingGuard.email || '',
        phone: editingGuard.phone || '',
        username: editingGuard.username || '',
        employee_code: editingGuard.employee_code || '',
        status: editingGuard.status || 'active',
        date_of_joining: editingGuard.date_of_joining || '',
        notes: editingGuard.notes || '',
        password: '',
      });
    } else {
      setPasswordMode('auto');
      const nextCode = getNextEmployeeCode(totalGuards);
      const autoPwd = generatePassword();

      reset({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        username: '',
        employee_code: nextCode,
        status: 'active',
        date_of_joining: '',
        notes: '',
        password: autoPwd,
      });
    }
  }, [isOpen, editingGuard, totalGuards, reset, clearErrors]);

  const handlePasswordModeChange = (mode) => {
    setPasswordMode(mode);
    clearErrors('password');

    if (mode === 'auto') {
      const newPwd = generatePassword();
      setValue('password', newPwd, {
        shouldValidate: true,
        shouldDirty: true,
      });
      setShowPassword(false);
    }
  };

  const handleCopyPassword = async () => {
    try {
      if (!passwordValue) return;
      await navigator.clipboard.writeText(passwordValue);
    } catch (e) {
      console.error('Clipboard copy failed', e);
    }
  };

  const onSubmitInternal = (data) => {
    if (!editingGuard && !data.password) {
      setError('password', {
        type: 'manual',
        message: 'Password is required for new guard',
      });
      return;
    }
    onSubmit(data, { editingGuard });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-40 flex">
      {/* Backdrop */}
      <div
        className="flex-1 bg-slate-900/40 backdrop-blur-sm"
        onClick={() => {
          if (!saving) onClose();
        }}
      />

      {/* Panel */}
      <div className="h-full max-h-screen w-full max-w-xl overflow-y-auto bg-slate-50 shadow-2xl">
        {/* Header / hero */}
        <div
          className="border-b border-slate-200 px-6 py-4"
          style={{
            background: `linear-gradient(135deg, ${primary600} 0%, ${secondary600} 100%)`,
          }}
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs font-semibold tracking-wide text-white/70 uppercase">
                {editingGuard ? 'Update guard profile' : 'Create new guard'}
              </p>
              <h3 className="mt-1 text-xl font-bold text-white">
                {editingGuard ? 'Edit Guard' : 'Add Guard'}
              </h3>
              <p className="mt-2 text-xs sm:text-sm text-white/80 max-w-md">
                Manage guard details, login credentials and status. Guards use
                these credentials to log into the mobile app.
              </p>
            </div>
            <button
              className="mt-1 rounded-full bg-white/10 p-2 text-white hover:bg-white/20"
              onClick={() => {
                if (!saving) onClose();
              }}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Form body */}
        <form
          onSubmit={handleSubmit(onSubmitInternal)}
          className="space-y-6 px-6 py-6"
        >
          {/* SECTION: Basic info */}
          <section className="rounded-2xl bg-white p-4 shadow-sm border border-slate-100">
            <h4 className="mb-4 text-sm font-semibold text-slate-800">
              Basic information
            </h4>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="mb-1 block text-[13px] font-semibold text-slate-700">
                  First Name <span className="text-rose-500">*</span>
                </label>
                <input
                  {...register('first_name')}
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm focus:outline-none transition-all"
                  style={{ borderColor: 'rgb(226 232 240)' }}
                  onFocus={applyInputFocusStyle}
                  onBlur={resetInputFocusStyle}
                />
                {errors.first_name && (
                  <p className="mt-1 text-[11px] text-rose-500">
                    {errors.first_name.message}
                  </p>
                )}
              </div>
              <div>
                <label className="mb-1 block text-[13px] font-semibold text-slate-700">
                  Last Name
                </label>
                <input
                  {...register('last_name')}
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm focus:outline-none transition-all"
                  style={{ borderColor: 'rgb(226 232 240)' }}
                  onFocus={applyInputFocusStyle}
                  onBlur={resetInputFocusStyle}
                />
                {errors.last_name && (
                  <p className="mt-1 text-[11px] text-rose-500">
                    {errors.last_name.message}
                  </p>
                )}
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="mb-1 block text-[13px] font-semibold text-slate-700">
                  Phone <span className="text-rose-500">*</span>
                </label>
                <input
                  {...register('phone')}
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm focus:outline-none transition-all"
                  style={{ borderColor: 'rgb(226 232 240)' }}
                  onFocus={applyInputFocusStyle}
                  onBlur={resetInputFocusStyle}
                />
                {errors.phone && (
                  <p className="mt-1 text-[11px] text-rose-500">
                    {errors.phone.message}
                  </p>
                )}
              </div>
              <div>
                <label className="mb-1 block text-[13px] font-semibold text-slate-700">
                  Email <span className="text-rose-500">*</span>
                </label>
                <input
                  type="email"
                  {...register('email')}
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm focus:outline-none transition-all"
                  style={{ borderColor: 'rgb(226 232 240)' }}
                  onFocus={applyInputFocusStyle}
                  onBlur={resetInputFocusStyle}
                />
                {errors.email && (
                  <p className="mt-1 text-[11px] text-rose-500">
                    {errors.email.message}
                  </p>
                )}
              </div>
            </div>
          </section>

          {/* SECTION: Login & password */}
          <section className="rounded-2xl bg-white p-4 shadow-sm border border-slate-100">
            <h4 className="mb-4 text-sm font-semibold text-slate-800">
              Login & credentials
            </h4>

            {/* Username */}
            <div className="mb-4">
              <label className="mb-1 block text-[13px] font-semibold text-slate-700">
                Username <span className="text-rose-500">*</span>
              </label>
              <input
                {...register('username')}
                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm focus:outline-none transition-all"
                style={{ borderColor: 'rgb(226 232 240)' }}
                onFocus={applyInputFocusStyle}
                onBlur={resetInputFocusStyle}
              />
              <p className="mt-1 text-[11px] text-slate-500">
                Guards will use this username to log in to the Patrol Visit app.
              </p>
              {errors.username && (
                <p className="mt-1 text-[11px] text-rose-500">
                  {errors.username.message}
                </p>
              )}
            </div>

            {/* Password mode toggle */}
            <div className="mb-3 flex flex-wrap items-center gap-2 text-[12px] font-medium">
              <span className="text-slate-600">Password mode:</span>
              <button
                type="button"
                onClick={() => handlePasswordModeChange('auto')}
                className={`rounded-full border px-3 py-1.5 text-xs ${
                  passwordMode === 'auto'
                    ? 'border-slate-900 bg-slate-900 text-white'
                    : 'border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100'
                }`}
              >
                Auto-generate
              </button>
              <button
                type="button"
                onClick={() => handlePasswordModeChange('manual')}
                className={`rounded-full border px-3 py-1.5 text-xs ${
                  passwordMode === 'manual'
                    ? 'border-slate-900 bg-slate-900 text-white'
                    : 'border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100'
                }`}
              >
                Manual entry
              </button>
            </div>

            {/* Password input */}
            <div>
              <label className="mb-1 block text-[13px] font-semibold text-slate-700">
                Password{' '}
                {editingGuard ? (
                  <span className="text-[11px] text-slate-500">
                    (leave blank to keep existing)
                  </span>
                ) : (
                  <span className="text-rose-500">*</span>
                )}
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  {...register('password')}
                  placeholder={
                    editingGuard
                      ? 'Enter new password (optional)'
                      : 'Set initial password'
                  }
                  readOnly={passwordMode === 'auto'}
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 pr-24 text-sm focus:outline-none transition-all"
                  style={{ borderColor: 'rgb(226 232 240)' }}
                  onFocus={applyInputFocusStyle}
                  onBlur={resetInputFocusStyle}
                />
                <div className="absolute inset-y-0 right-2 flex items-center gap-1">
                  {passwordMode === 'auto' && passwordValue && (
                    <button
                      type="button"
                      onClick={handleCopyPassword}
                      className="flex h-8 w-8 items-center justify-center rounded-lg hover:bg-slate-100"
                      title="Copy password"
                    >
                      <Copy className="h-4 w-4 text-slate-600" />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="flex h-8 w-8 items-center justify-center rounded-lg hover:bg-slate-100"
                    title={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-slate-600" />
                    ) : (
                      <Eye className="h-4 w-4 text-slate-600" />
                    )}
                  </button>
                </div>
              </div>

              {/* Strength meter */}
              {passwordValue && (
                <div className="mt-2 flex items-center justify-between text-[11px]">
                  <div className="flex items-center gap-1">
                    {[1, 2, 3].map((lvl) => (
                      <div
                        key={lvl}
                        className={`h-1.5 w-8 rounded-full ${
                          passwordStrength.score >= lvl
                            ? passwordStrength.colorClass
                            : 'bg-slate-200'
                        }`}
                      />
                    ))}
                  </div>
                  <span
                    className={`font-medium ${passwordStrength.textClass}`}
                  >
                    {passwordStrength.label}
                  </span>
                </div>
              )}

              {errors.password && (
                <p className="mt-1 text-[11px] text-rose-500">
                  {errors.password.message}
                </p>
              )}
            </div>
          </section>

          {/* SECTION: Employment details */}
          <section className="rounded-2xl bg-white p-4 shadow-sm border border-slate-100">
            <h4 className="mb-4 text-sm font-semibold text-slate-800">
              Employment details
            </h4>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="mb-1 block text-[13px] font-semibold text-slate-700">
                  Employee Code
                </label>
                <input
                  {...register('employee_code')}
                  readOnly
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-700 focus:outline-none"
                />
                <p className="mt-1 text-[11px] text-slate-500">
                  Auto-generated as GUARD001, GUARD002… (read only here).
                </p>
                {errors.employee_code && (
                  <p className="mt-1 text-[11px] text-rose-500">
                    {errors.employee_code.message}
                  </p>
                )}
              </div>

              <div>
                <label className="mb-1 block text-[13px] font-semibold text-slate-700">
                  Status
                </label>
                <select
                  {...register('status')}
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm focus:outline-none transition-all"
                  style={{ borderColor: 'rgb(226 232 240)' }}
                  onFocus={applyInputFocusStyle}
                  onBlur={resetInputFocusStyle}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
                {errors.status && (
                  <p className="mt-1 text-[11px] text-rose-500">
                    {errors.status.message}
                  </p>
                )}
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-[1fr_2fr]">
              <div>
                <label className="mb-1 block text-[13px] font-semibold text-slate-700">
                  Date of Joining
                </label>
                <input
                  type="date"
                  {...register('date_of_joining')}
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm focus:outline-none transition-all"
                  style={{ borderColor: 'rgb(226 232 240)' }}
                  onFocus={applyInputFocusStyle}
                  onBlur={resetInputFocusStyle}
                />
                {errors.date_of_joining && (
                  <p className="mt-1 text-[11px] text-rose-500">
                    {errors.date_of_joining.message}
                  </p>
                )}
              </div>

              <div>
                <label className="mb-1 block text-[13px] font-semibold text-slate-700">
                  Notes
                </label>
                <textarea
                  rows={3}
                  {...register('notes')}
                  className="w-full resize-none rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm focus:outline-none transition-all"
                  style={{ borderColor: 'rgb(226 232 240)' }}
                  onFocus={applyInputFocusStyle}
                  onBlur={resetInputFocusStyle}
                />
                {errors.notes && (
                  <p className="mt-1 text-[11px] text-rose-500">
                    {errors.notes.message}
                  </p>
                )}
              </div>
            </div>
          </section>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={() => {
                if (!saving) onClose();
              }}
              className="rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition-all hover:bg-slate-100"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="rounded-lg px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:shadow-lg disabled:opacity-60"
              style={{
                background: `linear-gradient(135deg, ${primary600} 0%, ${secondary600} 100%)`,
              }}
            >
              {saving
                ? 'Saving...'
                : editingGuard
                ? 'Save Changes'
                : 'Create Guard'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GuardFormSlideOver;
